import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
} from "@mui/material";
import { DeleteOutline } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { LuRefreshCw } from "react-icons/lu";
import { HiPlus } from "react-icons/hi";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import SearchBar from "../../../../components/SearchBar";
import { CustomNoRowsOverlay } from "../../../../components/common/OverlayGrid";
import {
  useSyncZohoInvoicesMutation,
  useFetchInvoicesQueueQuery,
  useFetchInvoiceSourcesQuery,
  useFetchInvoiceProjectsQuery,
  useDeleteManualInvoiceMutation,
  useLazyFetchInvoiceByIdQuery,
} from "../../../../slice/logdetailSlice";
import { format } from "date-fns";
import { useTheme } from "../../../../ThemeContext";

// Modular Screen Components
import ManualEntryModal from "./ManualEntryModal";
import ContractMappingModal from "../../contract/ContractMappingModal";
import ReviewMatchesModal from "../../contract/ReviewMatchesModal";
import FixedCostMappingModal from "../../fixedCost/FixedCostMappingModal";
import FinalAllocationModal from "../../fixedCost/FinalAllocationModal";
import { getBudgetTypeInfo } from "../../../../utils/budgetTypeUtils";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : format(d, "dd MMM yyyy");
  } catch {
    return dateStr;
  }
};

const formatDateTime = (dateStr) => {
  if (!dateStr || dateStr === "—") return "—";
  try {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : format(d, "dd-MM-yyyy, hh:mm a");
  } catch {
    return dateStr;
  }
};

const stripHtml = (html) => {
  if (!html || html === "—") return "";
  const text = String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text;
};

const formatAmount = (amt) => {
  if (amt === null || amt === undefined) return "0.00";
  const num = Number(amt);
  return isNaN(num)
    ? amt
    : num.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
};

const InvoiceQueueTab = ({ viewingInvoice: propViewingInvoice, setViewingInvoice: propSetViewingInvoice }) => {
  const { isDarkMode } = useTheme();
  const isDark = isDarkMode;
  const [sourceFilter, setSourceFilter] = useState("All sources");
  const [statusFilter, setStatusFilter] = useState("All statuses");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [manualRows, setManualRows] = useState([]);

  const [syncZohoInvoices, { isLoading: isSyncing }] = useSyncZohoInvoicesMutation();
  const [deleteManualInvoice] = useDeleteManualInvoiceMutation();
  const [fetchInvoiceById] = useLazyFetchInvoiceByIdQuery();

  // Debounce search input for responsive typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch Sources from API: GET /invoices/sources
  const { data: sourcesResponse, refetch: refetchSources } = useFetchInvoiceSourcesQuery();

  const sourceOptions = useMemo(() => {
    const apiSources = sourcesResponse?.data;
    if (Array.isArray(apiSources) && apiSources.length > 0) {
      const cleanSources = apiSources.map((s) => (s === "MANINV" ? "MANUAL" : s));
      const list = ["All sources", ...new Set(cleanSources)];
      if (!list.includes("MANUAL")) list.push("MANUAL");
      return list;
    }
    return ["All sources", "TT", "INV", "TEST", "MANUAL"];
  }, [sourcesResponse]);

  // Fetch Invoices Queue from API: GET /invoices?search&source&status&from_date&to_date
  const queryArgs = useMemo(() => {
    const params = {};
    if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
    if (sourceFilter && sourceFilter !== "All sources") {
      params.source = sourceFilter === "MANUAL" ? "MANINV" : sourceFilter;
    }
    if (statusFilter && statusFilter !== "All statuses") params.status = statusFilter.toLowerCase();
    return params;
  }, [debouncedSearch, sourceFilter, statusFilter]);

  const {
    data: invoicesResponse,
    isLoading: isInvoicesLoading,
    isFetching: isInvoicesFetching,
    refetch: refetchInvoices,
  } = useFetchInvoicesQueueQuery(queryArgs);

  // Fetch all projects for matching and metadata
  const { data: projectsResponse } = useFetchInvoiceProjectsQuery();
  const projectList = useMemo(() => projectsResponse?.data || [], [projectsResponse]);

  const [syncMetadata, setSyncMetadata] = useState(() => {
    try {
      const saved = localStorage.getItem("re_invoice_sync_metadata");
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    return {
      lastSync: "04 Sep 2026, 01:00",
      totalCount: 56,
      ttCount: 42,
      tmCount: 11,
      hufCount: 3,
      createdCount: 0,
      skippedCount: 0,
    };
  });

  // Modal Visibility States
  const [showManualModal, setShowManualModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [showReviewMatchesModal, setShowReviewMatchesModal] = useState(false);
  const [showFixedModal, setShowFixedModal] = useState(false);
  const [showFinalSplitModal, setShowFinalSplitModal] = useState(false);
  const [isAllocatingFinalSplit, setIsAllocatingFinalSplit] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [localViewingInvoice, setLocalViewingInvoice] = useState(null);
  const viewingInvoice = propViewingInvoice !== undefined ? propViewingInvoice : localViewingInvoice;
  const setViewingInvoice = propSetViewingInvoice || setLocalViewingInvoice;

  // Map API response to DataGrid row items
  const apiRows = useMemo(() => {
    if (!invoicesResponse?.data || !Array.isArray(invoicesResponse.data)) {
      return [];
    }

    return invoicesResponse.data.map((item, index) => {
      const id = item.invoice_id ?? item.id ?? index + 1;
      const statusFormatted = item.status
        ? item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase()
        : "Unmapped";
      const isUnmapped = statusFormatted.toLowerCase() === "unmapped";

      // Match project name from projectList if item.project_id is available
      const matchedProject = projectList.find(
        (p) => String(p.project_id) === String(item.project_id) || String(p.id) === String(item.project_id)
      );
      const projectName = item.project_name || matchedProject?.title || null;
      const rawBudget = item.budget_type || matchedProject?.budget_type || null;
      const budgetInfo = getBudgetTypeInfo(rawBudget);

      const clientName = item.client_name || matchedProject?.client_name || projectName || "—";

      return {
        id,
        source: item.source || "—",
        invoice: item.invoice_number || `INV-${id}`,
        date: formatDate(item.invoiced_date || item.payment_date || item.created_at),
        client: clientName,
        amount: formatAmount(item.total_amount),
        project: projectName,
        project_id: item.project_id || "—",
        budget_type: rawBudget,
        model: budgetInfo.model,
        status: statusFormatted,
        action: isUnmapped ? "Map" : "View",
        payment_mode: item.payment_mode || "—",
        reference: item.reference_number || "—",
        description: item.description || "—",
        created_by_name: item.created_by_name || "—",
        created_at: formatDateTime(item.created_at),
        rawItem: item,
      };
    });
  }, [invoicesResponse, projectList]);

  // Combined rows (manual entries + API rows directly from backend response)
  const filteredData = useMemo(() => {
    const existingIds = new Set(apiRows.map((r) => String(r.id)));
    const existingInvoices = new Set(apiRows.map((r) => String(r.invoice)));
    const uniqueManualRows = manualRows.filter(
      (m) => !existingIds.has(String(m.id)) && !existingInvoices.has(String(m.invoice))
    );

    let rows = [...uniqueManualRows, ...apiRows];

    // Ensure completely unique rows by ID and invoice number
    const seen = new Set();
    rows = rows.filter((r) => {
      const key = `${r.id}_${r.invoice}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (sourceFilter && sourceFilter !== "All sources") {
      rows = rows.filter((r) => {
        const rowSource = String(r.source || "").toUpperCase();
        const filterVal = sourceFilter.toUpperCase();
        if (filterVal === "MANUAL" || filterVal === "CASH") {
          return rowSource === "MANUAL" || rowSource === "CASH" || rowSource === "MANINV";
        }
        return rowSource === filterVal;
      });
    }
    return rows;
  }, [apiRows, manualRows, sourceFilter]);

  // Clean up manualRows that are already returned by the API
  useEffect(() => {
    if (invoicesResponse?.data && manualRows.length > 0) {
      const apiInvIds = new Set(
        invoicesResponse.data.map((item) => String(item.invoice_id ?? item.id))
      );
      const apiInvNums = new Set(
        invoicesResponse.data.map((item) => String(item.invoice_number))
      );
      setManualRows((prev) =>
        prev.filter((m) => !apiInvIds.has(String(m.id)) && !apiInvNums.has(String(m.invoice)))
      );
    }
  }, [invoicesResponse, manualRows.length]);

  const handleSyncNow = async () => {
    try {
      const today = format(new Date(), "yyyy-MM-dd");
      const response = await syncZohoInvoices(today).unwrap();

      if (response && (response.status || response.count !== undefined)) {
        const now = new Date();
        const formattedDate = format(now, "dd MMM yyyy");
        const formattedTime = format(now, "hh:mm a");
        const updatedTimestamp = `${formattedDate}, ${formattedTime}`;

        const totalCount = response.count ?? 0;
        const createdCount = response.created ?? 0;
        const skippedCount = response.skipped ?? 0;

        let ttCount = 0;
        let tmCount = 0;
        let hufCount = 0;
        if (response.organizations) {
          const orgValues = Object.values(response.organizations);
          if (orgValues.length > 0) ttCount = orgValues[0]?.count ?? 0;
          if (orgValues.length > 1) tmCount = orgValues[1]?.count ?? 0;
          if (orgValues.length > 2) hufCount = orgValues[2]?.count ?? 0;
        }

        const newMeta = {
          lastSync: updatedTimestamp,
          totalCount,
          createdCount,
          skippedCount,
          ttCount,
          tmCount,
          hufCount,
        };

        setSyncMetadata(newMeta);
        localStorage.setItem("re_invoice_sync_metadata", JSON.stringify(newMeta));

        refetchInvoices();
        refetchSources();

        toast.success(
          response.message || `Invoices synchronized successfully at ${updatedTimestamp}!`
        );
      } else {
        toast.error(response?.message || "Failed to sync invoices");
      }
    } catch (err) {
      console.error("Zoho Sync error:", err);
      toast.error(err?.data?.message || err?.message || "Failed to sync invoices from Zoho");
    }
  };

  const handleSaveManualEntry = (newEntry) => {
    setManualRows((prev) => {
      const exists = prev.some(
        (r) => String(r.id) === String(newEntry.id) || String(r.invoice) === String(newEntry.invoice)
      );
      if (exists) return prev;
      return [newEntry, ...prev];
    });
    // Save & go to mapping: Transition directly to the mapping view for this newly added manual cash payment
    setSelectedInvoice(newEntry);
    setViewingInvoice(newEntry);
    refetchInvoices?.();
  };

  const handleActionClick = useCallback(
    async (item) => {
      setSelectedInvoice(item);
      if (item.model === "Fixed" && item.status === "Allocated") {
        const invId = item.rawItem?.invoice_id || item.rawItem?.id || item.id;
        if (invId) {
          try {
            const res = await fetchInvoiceById(Number(invId)).unwrap();
            const invData = res?.data || res;
            const resources = invData?.resources || invData?.invoice_resources;
            if (Array.isArray(resources) && resources.length > 0) {
              setSelectedInvoice({ ...item, ...invData });
              setShowFinalSplitModal(true);
              return;
            }
            // Receipt has no allocated resources -> display mapped receipts popup modal
            setSelectedInvoice({ ...item, ...invData });
            setShowFixedModal(true);
            return;
          } catch (err) {
            console.error("fetchInvoiceById error in handleActionClick:", err);
            setSelectedInvoice(item);
            setShowFixedModal(true);
            return;
          }
        }
        setShowFinalSplitModal(true);
      } else {
        // Both View and Map now navigate inline to full page view
        setViewingInvoice(item);
      }
    },
    [setViewingInvoice, fetchInvoiceById]
  );

  // Confirm Contract Allocation (Screen 2)
  const handleConfirmContractAllocation = () => {
    setShowContractModal(false);
    setViewingInvoice(null);
    setShowReviewMatchesModal(false);
    refetchInvoices?.();
  };

  // Save Fixed Mapping (Screen 3)
  const handleSaveFixedMapping = () => {
    setShowFixedModal(false);
    setViewingInvoice(null);
    refetchInvoices?.();
  };

  // Confirm Final Allocation at Project Completion (Screen 3b)
  const handleConfirmFinalAllocation = () => {
    setIsAllocatingFinalSplit(false);
    setShowFinalSplitModal(false);
    setViewingInvoice(null);
    refetchInvoices?.();
  };

  const handleDeleteManualInvoice = useCallback(
    async (row) => {
      const invId = row.rawItem?.invoice_id || row.rawItem?.id || row.id;
      const invNum = row.invoice || row.rawItem?.invoice_number || `ID #${invId}`;

      const result = await Swal.fire({
        title: "Delete Manual Invoice?",
        text: `Are you sure you want to delete invoice "${invNum}"? This action cannot be undone.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#ef4444",  
        cancelButtonColor: "#04a189",
        confirmButtonText: "Yes, Delete",
        cancelButtonText: "Cancel",
        reverseButtons: true,
      });

      if (result.isConfirmed) {
        try {
          const response = await deleteManualInvoice(invId).unwrap();
          toast.success(response?.message || "Manual invoice deleted successfully");
          setManualRows((prev) =>
            prev.filter(
              (r) => String(r.id) !== String(invId) && String(r.invoice) !== String(invNum)
            )
          );
          refetchInvoices?.();
          refetchSources?.();
        } catch (err) {
          console.error("Delete manual invoice error:", err);
          toast.error(err?.data?.message || err?.message || "Failed to delete manual invoice");
        }
      }
    },
    [deleteManualInvoice, refetchInvoices, refetchSources]
  );

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const columns = useMemo(() => [
    {
      field: "deleteAction",
      headerName: "Action",
      width: 70,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const rawSource = String(
          params.row.source || params.row.rawItem?.source || ""
        ).toUpperCase();
        const isManual =
          rawSource === "MANUAL" ||
          rawSource === "CASH" ||
          rawSource === "MANINV" ||
          String(params.row.invoice || "").toUpperCase().startsWith("MAN");
        const isMapped = String(params.row.status || "").toLowerCase() === "mapped";
        const canDelete = isManual && isMapped;

        if (!canDelete) return null;

        return (
          <Tooltip title="Delete manual invoice" arrow placement="top">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteManualInvoice(params.row);
              }}
              sx={{
                color: "#ef4444",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(239, 68, 68, 0.08)",
                  color: "#dc2626",
                },
              }}
            >
              <DeleteOutline sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "project_id",
      headerName: "Project ID",
      width: 100,
      renderCell: (params) => {
        if (!params.value || params.value === "—") return <span className="text-muted">—</span>;
        return <span style={{ fontWeight: 500 }}>{params.value}</span>;
      },
    },
    {
      field: "source",
      headerName: "Source",
      width: 90,
      renderCell: (params) => {
        const val = String(params.value || "").toUpperCase();
        if (val === "CASH" || val === "MANUAL" || val === "MANINV") {
          return <span className="badge-source-manual">MANUAL</span>;
        }
        return <span className="badge-source-tt">{params.value}</span>;
      },
    },
    {
      field: "invoice",
      headerName: "Invoice",
      width: 130,
    },
    {
      field: "date",
      headerName: "Date",
      width: 120,
    },
    {
      field: "client",
      headerName: "Client",
      minWidth: 160,
      flex: 1,
      renderCell: (params) => {
        const val = params.value;
        if (!val || val === "—" || val === "-") {
          return <span>{params.row.project || "—"}</span>;
        }
        return <span>{val}</span>;
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 130,
    },
    {
      field: "project",
      headerName: "Project",
      minWidth: 250,
      flex: 1.5,
      renderCell: (params) => {
        if (!params.value) {
          return <span className="cell-not-set">— not set —</span>;
        }
        return <span>{params.value}</span>;
      },
    },
    {
      field: "model",
      headerName: "Model",
      width: 115,
      renderCell: (params) => {
        const budgetType = params.row.budget_type;
        if (budgetType === "FIXED_COST") {
          return <span className="badge-model-fixed">Fixed Cost</span>;
        }
        if (budgetType === "TIME_AND_MATERIAL") {
          return <span className="badge-model-contract">T&M(Contract)</span>;
        }
        if (budgetType === "CONTRACT") {
          return <span className="badge-model-contract">Contract</span>;
        }
        return <span className="text-muted">—</span>;
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => {
        if (params.value === "Unmapped") {
          return <span className="badge-status-unmapped">Unmapped</span>;
        }
        if (params.value === "Mapped") {
          return <span className="badge-status-mapped">Mapped</span>;
        }
        return <span className="badge-status-allocated">Allocated</span>;
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 90,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const isMappedOrAllocated =
          params.row.status === "Mapped" || params.row.status === "Allocated";
        return (
          <button
            type="button"
            className={isMappedOrAllocated ? "btn-action-view btn-allocate-hover" : "btn-action-map"}
            onClick={() => handleActionClick(params.row)}
          >
            {params.row.action}
          </button>
        );
      },
    },
    {
      field: "payment_mode",
      headerName: "Payment Mode",
      width: 140,
      renderCell: (params) => {
        if (!params.value || params.value === "—") return <span className="text-muted">—</span>;
        return <span>{params.value}</span>;
      },
    },
    {
      field: "reference",
      headerName: "Reference",
      width: 150,
      renderCell: (params) => {
        if (!params.value || params.value === "—") return <span className="text-muted">—</span>;
        return <span style={{ fontWeight: 500 }}>{params.value}</span>;
      },
    },
    {
      field: "description",
      headerName: "Description",
      minWidth: 180,
      flex: 1.5,
      renderCell: (params) => {
        const clean = stripHtml(params.value);
        if (!clean) return <span className="text-muted">—</span>;

        const isLong = clean.length > 20;
        const truncated = isLong ? `${clean.slice(0, 20)}...` : clean;

        return (
          <Tooltip title={clean} arrow placement="top" disableInteractive>
            <span
              style={{
                cursor: isLong ? "help" : "default",
                maxWidth: "100%",
                display: "inline-block",
              }}
            >
              {truncated}
            </span>
          </Tooltip>
        );
      },
    },
    {
      field: "created_by_name",
      headerName: "Created By",
      minWidth: 150,
      flex: 1,
      renderCell: (params) => {
        if (!params.value || params.value === "—") return <span className="text-muted">—</span>;
        return <span>{params.value}</span>;
      },
    },
    {
      field: "created_at",
      headerName: "Created At",
      width: 170,
      renderCell: (params) => {
        if (!params.value || params.value === "—") return <span className="text-muted">—</span>;
        return <span>{formatDateTime(params.value)}</span>;
      },
    },
  ], [handleActionClick, handleDeleteManualInvoice]);

  const isFixedCostInvoice = useMemo(() => {
    if (!viewingInvoice) return false;
    const bType = (
      viewingInvoice?.budget_type ||
      viewingInvoice?.rawItem?.budget_type ||
      viewingInvoice?.rawItem?.project?.budget_type ||
      ""
    ).toUpperCase();
    if (viewingInvoice?.model === "Fixed" || bType === "FIXED" || bType === "FIXED_COST") {
      return true;
    }
    if (viewingInvoice?.project_id && Array.isArray(projectList)) {
      const proj = projectList.find(
        (p) => String(p.project_id) === String(viewingInvoice.project_id) || String(p.id) === String(viewingInvoice.project_id)
      );
      const projBType = (proj?.budget_type || "").toUpperCase();
      if (projBType === "FIXED" || projBType === "FIXED_COST") {
        return true;
      }
    }
    return false;
  }, [viewingInvoice, projectList]);

  return (
    <div>
      {viewingInvoice ? (
        isAllocatingFinalSplit ? (
          <FinalAllocationModal
            isInline={true}
            invoice={selectedInvoice || viewingInvoice}
            onHide={() => setIsAllocatingFinalSplit(false)}
            onBackToScope={() => setIsAllocatingFinalSplit(false)}
            onConfirmAllocation={handleConfirmFinalAllocation}
          />
        ) : isFixedCostInvoice ? (
          <FixedCostMappingModal
            isInline={true}
            show={true}
            invoice={viewingInvoice}
            onHide={() => {
              setViewingInvoice(null);
              setIsAllocatingFinalSplit(false);
            }}
            existingInvoices={filteredData}
            onSaveMapping={handleSaveFixedMapping}
            onOpenFinalSplit={(extra) => {
              setSelectedInvoice({
                ...viewingInvoice,
                ...(extra || {}),
              });
              setIsAllocatingFinalSplit(true);
            }}
            onSwitchToContract={() => {
              setViewingInvoice({
                ...viewingInvoice,
                model: "Contract",
                budget_type: "CONTRACT",
              });
            }}
          />
        ) : (
          <ContractMappingModal
            isInline={true}
            invoice={viewingInvoice}
            existingInvoices={filteredData}
            onHide={() => {
              setViewingInvoice(null);
              setIsAllocatingFinalSplit(false);
            }}
            onConfirmAllocate={handleConfirmContractAllocation}
            onSaveFixedMapping={handleSaveFixedMapping}
            onOpenReviewMatches={(extra) => {
              setSelectedInvoice({
                ...viewingInvoice,
                ...(extra || {}),
              });
              setShowReviewMatchesModal(true);
            }}
            onOpenFinalSplit={(extra) => {
              setSelectedInvoice({
                ...viewingInvoice,
                ...(extra || {}),
              });
              setIsAllocatingFinalSplit(true);
            }}
            onSwitchToFixed={() => {
              setViewingInvoice({
                ...viewingInvoice,
                model: "Fixed",
                budget_type: "FIXED_COST",
              });
            }}
          />
        )
      ) : (
        <>
          {/* Action and Filter Bar Box */}
          <Box
        className="d-flex flex-wrap align-items-center justify-content-between gap-3"
        sx={{
          border: isDark ? "1px solid #333" : "1px solid #e0e0e0",
          borderRadius: "6px",
          padding: "10px 16px",
          marginBottom: "12px",
          backgroundColor: isDark ? "transparent" : "#fff",
        }}
      >
        <div className="d-flex flex-wrap align-items-center gap-3">
          {/* MUI Select: Source */}
          <FormControl size="small" sx={{ width: 140 }}>
            <Select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                width: 140,
                backgroundColor: isDark ? "transparent" : "#fff",
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: 140,
                  },
                },
              }}
            >
              {sourceOptions.map((source) => (
                <MenuItem key={source} value={source}>
                  {source}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* MUI Select: Status */}
          <FormControl size="small" sx={{ width: 140 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
              size="small"
              sx={{
                width: 140,
                backgroundColor: isDark ? "transparent" : "#fff",
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    width: 140,
                  },
                },
              }}
            >
              <MenuItem value="All statuses">
                All statuses
              </MenuItem>
              <MenuItem value="Unmapped">
                Unmapped
              </MenuItem>
              <MenuItem value="Mapped">
                Mapped
              </MenuItem>
              <MenuItem value="Allocated">
                Allocated
              </MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Right Side: Search and Actions */}
        <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
          <SearchBar
            placeholder="Search invoice"
            searchTerm={searchTerm}
            onSearchChange={(e) => setSearchTerm(e.target.value)}
          />

          <button
            type="button"
            className="re-btn-sync"
            onClick={handleSyncNow}
            disabled={isSyncing}
          >
            <LuRefreshCw
              className={isSyncing ? "spin-animation" : ""}
            />
            {isSyncing ? "Syncing..." : "Sync now"}
          </button>

          <button
            type="button"
            className="re-btn-manual"
            onClick={() => setShowManualModal(true)}
          >
            <HiPlus size={16} />
            Manual entry
          </button>
        </div>
      </Box>

      {/* Sync Metadata Subtitle */}
      <div
        className="re-sync-metadata"
        style={{
          textAlign: "left",
          fontSize: "12.5px",
          color: isDark ? "#9ca3af" : "#6b7280",
          margin: "12px 0",
          paddingLeft: "2px",
          width: "100%",
        }}
      >
        Last sync {syncMetadata.lastSync} · TT {syncMetadata.ttCount} · TM {syncMetadata.tmCount} · HUF {syncMetadata.hufCount} invoices ({syncMetadata.totalCount ?? (syncMetadata.ttCount + syncMetadata.tmCount + syncMetadata.hufCount)} total) · {syncMetadata.skippedCount ?? 0} duplicates skipped
      </div>

      {/* Material-UI DataGrid Table */}
      <Box
        sx={{
          width: "100%",
          backgroundColor: isDark ? "transparent" : "#fff",
        }}
      >
        <DataGrid
          rows={filteredData}
          columns={columns}
          autoHeight
          loading={isInvoicesLoading || isInvoicesFetching}
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          slots={{ noRowsOverlay: CustomNoRowsOverlay }}
          sx={{
            border: isDark ? "1px solid #333" : "1px solid #e0e0e0",
            borderRadius: "6px",
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark ? "transparent" : "#fff",
              borderBottom: isDark ? "1px solid #333" : "1px solid #e0e0e0",
              fontSize: "13px",
              fontWeight: 600,
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              borderBottom: isDark ? "1px solid #333" : "1px solid #f0f0f0",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "#fafafa",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: isDark ? "1px solid #333" : "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "flex-end",
            },
            "& .MuiTablePagination-root": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              marginLeft: "auto",
            },
            "& .MuiTablePagination-toolbar": {
              display: "flex !important",
              alignItems: "center !important",
              justifyContent: "flex-end !important",
              minHeight: "52px",
              paddingRight: "16px",
              marginLeft: "auto !important",
            },
            "& .MuiTablePagination-toolbar p, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
              marginTop: "0 !important",
              marginBottom: "0 !important",
              lineHeight: "1.5 !important",
              display: "inline-flex !important",
              alignItems: "center !important",
            },
            "& .MuiTablePagination-select, & .MuiTablePagination-input, & .MuiDataGrid-footerContainer .MuiInputBase-root, & .MuiDataGrid-footerContainer .MuiSelect-select": {
              backgroundColor: "transparent !important",
              display: "inline-flex !important",
              alignItems: "center !important",
              paddingTop: "0 !important",
              paddingBottom: "0 !important",
              marginTop: "0 !important",
              marginBottom: "0 !important",
            },
            "& .MuiTablePagination-actions": {
              display: "inline-flex !important",
              alignItems: "center !important",
              marginLeft: "16px !important",
            },
          }}
        />
      </Box>
        </>
      )}

      {/* Screen 1b: Manual Cash Entry Modal */}
      <ManualEntryModal
        show={showManualModal}
        onHide={() => setShowManualModal(false)}
        onSaveEntry={handleSaveManualEntry}
        existingInvoices={filteredData}
      />

      {/* Screen 2: Contract Mapping & Auto-Allocate Modal */}
      {showContractModal && (
        <ContractMappingModal
          show={showContractModal}
          onHide={() => setShowContractModal(false)}
          invoice={selectedInvoice}
          existingInvoices={filteredData}
          onConfirmAllocate={handleConfirmContractAllocation}
          onOpenReviewMatches={(extra) => {
            if (extra) {
              setSelectedInvoice((prev) => ({
                ...(prev || selectedInvoice),
                ...(extra || {}),
              }));
            }
            setShowContractModal(false);
            setShowReviewMatchesModal(true);
          }}
          onSwitchToFixed={() => {
            setShowContractModal(false);
            setShowFixedModal(true);
          }}
        />
      )}

      {/* Screen 2b: Review & Correct Matches Modal */}
      {showReviewMatchesModal && (
        <ReviewMatchesModal
          show={showReviewMatchesModal}
          onHide={() => setShowReviewMatchesModal(false)}
          invoice={selectedInvoice}
          projectUsers={selectedInvoice?.availableUsers || selectedInvoice?.projectUsers || []}
          onConfirmAllocate={handleConfirmContractAllocation}
          onBackToContract={() => {
            setShowReviewMatchesModal(false);
          }}
        />
      )}

      {/* Screen 3: Fixed Cost Scope & CR Mapping Modal */}
      {showFixedModal && (
        <FixedCostMappingModal
          show={showFixedModal}
          onHide={() => setShowFixedModal(false)}
          invoice={selectedInvoice}
          existingInvoices={filteredData}
          onSaveMapping={handleSaveFixedMapping}
          onOpenFinalSplit={(extra) => {
            if (extra) {
              setSelectedInvoice((prev) => ({
                ...prev,
                ...extra,
              }));
            }
            setShowFixedModal(false);
            setShowFinalSplitModal(true);
          }}
          onSwitchToContract={() => {
            setShowFixedModal(false);
            setShowContractModal(true);
          }}
        />
      )}

      {/* Screen 3b: Project Complete Final Allocation Modal */}
      {showFinalSplitModal && (
        <FinalAllocationModal
          show={showFinalSplitModal}
          onHide={() => setShowFinalSplitModal(false)}
          invoice={selectedInvoice}
          onConfirmAllocation={handleConfirmFinalAllocation}
          onBackToScope={() => {
            setShowFinalSplitModal(false);
          }}
        />
      )}
    </div>
  );
};

export default InvoiceQueueTab;
